// 5. Internal types
import type { UserRole } from '../types/models'

// Role labels for display (sentence case, used in UI text and aria-labels)
export const ROLE_LABELS: Record<UserRole, string> = {
  SYSTEM_ADMIN: 'System admin',
  SCHOOL_ADMIN: 'School admin',
  TEACHER: 'Teacher',
  PARENT: 'Parent',
}

// Roles are labels, not warning or success states. Use one theme-aware treatment.
export const ROLE_BADGE_CLASS = 'border-primary/20 bg-accent text-accent-foreground hover:bg-accent'

/** Roles an admin can filter the directory and invitations by. */
export const ROLE_FILTER_OPTIONS: ReadonlyArray<{ value: UserRole; label: string }> = (
  ['SYSTEM_ADMIN', 'SCHOOL_ADMIN', 'TEACHER', 'PARENT'] as const
).map(value => ({ value, label: ROLE_LABELS[value] }))

/** True when any of the user's active roles is in `allowed`. */
export const hasAnyRole = (roles: readonly UserRole[], allowed: readonly UserRole[]): boolean =>
  roles.some(role => allowed.includes(role))

export const roleLabel = (role: string): string => ROLE_LABELS[role as UserRole] ?? role

/**
 * Active roles with the primary role first. Falls back to the legacy `role` field when the
 * API response (or a cached user) does not carry `roles`.
 */
export function getEffectiveRoles(user: {
  role?: UserRole | null
  roles?: UserRole[] | null
}): UserRole[] {
  const primary = user.role ?? null
  const all = user.roles?.length ? user.roles : primary ? [primary] : []
  if (primary && all.includes(primary)) return [primary, ...all.filter(role => role !== primary)]
  return primary ? [primary, ...all] : [...all]
}
